import {
  AppItem, DrawerProps, Hamburger,
  NavCategory,
  NavCategoryItem,
  NavDivider,
  NavDrawer,
  NavDrawerBody,
  NavDrawerHeader,
  NavItem,
  NavSectionHeader,
  NavSubItem,
  NavSubItemGroup, Tooltip,
  makeStyles,
  tokens, useRestoreFocusTarget
} from "@fluentui/react-components";
import {
  ChatBubblesQuestion20Filled,
  ChatBubblesQuestion20Regular,
  ChatMail20Filled,
  ChatMail20Regular,
  LightbulbPerson20Filled,
  LightbulbPerson20Regular,
  MathFormulaSparkle20Filled,
  MathFormulaSparkle20Regular,
  MegaphoneLoud20Filled,
  MegaphoneLoud20Regular,
  MoneyCalculator20Filled,
  MoneyCalculator20Regular,
  Premium20Filled,
  SettingsCogMultiple20Filled,
  SettingsCogMultiple20Regular,
  VideoClipMultiple20Filled,
  VideoClipMultiple20Regular,
  bundleIcon,
} from "@fluentui/react-icons";
import * as React from "react";
import ByColorForm from "../ByColor/ByColorForm";
import AnnouncementsPage from "../Pages/AnnouncementsPage";
import ReconcilerForm from "../Reconciler/ReconcilerForm";

const useStyles = makeStyles({
  root: {
    overflow: "hidden",
    display: "flex",
    height: "750px",
  },
  nav: {
    minWidth: "260px",
  },
  content: {
    flex: "1",
    padding: "16px",
    display: "grid",
    justifyContent: "flex-start",
    alignItems: "flex-start",
  },
  innerContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: tokens.spacingVerticalS,
    "& > *": {
      width: "100%",
      maxWidth: "420px",
    },
    "& > button": {
      width: "100%",
    },
    "& > img": {
      justifySelf: "center",
      width: "64px",
      height: "64px",
    },
  },
  field: {
    display: "flex",
    marginTop: "4px",
    marginLeft: "8px",
    flexDirection: "column",
    gridRowGap: tokens.spacingVerticalS,
  },
});

const Announcements = bundleIcon(MegaphoneLoud20Filled, MegaphoneLoud20Regular);
const FormulaByColor = bundleIcon(MathFormulaSparkle20Filled, MathFormulaSparkle20Regular);
const ChatMail = bundleIcon(ChatMail20Filled, ChatMail20Regular);
const ChatBubbleQuestion = bundleIcon(ChatBubblesQuestion20Filled, ChatBubblesQuestion20Regular);
const Video = bundleIcon(VideoClipMultiple20Filled, VideoClipMultiple20Regular);
const LightbulbPerson = bundleIcon(LightbulbPerson20Filled, LightbulbPerson20Regular);
const SettingsCogMultiple = bundleIcon(SettingsCogMultiple20Filled, SettingsCogMultiple20Regular);
const MoneyCalculator = bundleIcon(MoneyCalculator20Filled, MoneyCalculator20Regular);

type DrawerType = Required<DrawerProps>["type"];

interface BasicNavProps {
  content: React.ReactNode;
  setContent: CallableFunction;
}

const BasicNav: React.FC<BasicNavProps> = (props: BasicNavProps) => {
  const styles = useStyles();

  const [isOpen, setIsOpen] = React.useState(true);
  const [enabledLinks, setEnabledLinks] = React.useState(true);
  const [type, setType] = React.useState<DrawerType>("inline");
  const [isMultiple, setIsMultiple] = React.useState(true);

  // Tabster prop used to restore focus to the navigation trigger for overlay nav drawers
  const restoreFocusTargetAttributes = useRestoreFocusTarget();

  const linkDestination = enabledLinks ? "https://technbash.com" : "";

  return (
    <div className={styles.root}>
      <NavDrawer
        defaultSelectedValue="3"
        defaultSelectedCategoryValue=""
        open={isOpen}
        type={type}
        multiple={isMultiple}
        className={styles.nav}
      >
        <NavDrawerHeader>
          <Tooltip content="Close Navigation" relationship="label">
            <Hamburger onClick={() => setIsOpen(!isOpen)} />
          </Tooltip>
        </NavDrawerHeader>

        <NavDrawerBody>
          <AppItem
            // icon={<PersonCircle32Regular />}
            as="a"
            href={linkDestination}
          >
            tech+bash
          </AppItem>
          <NavDivider />
          <NavItem
            onClick={() => props.setContent(<AnnouncementsPage />)}
            icon={<Announcements />}
            value="1"
          >
            Announcements
          </NavItem>
          <NavDivider />
          <NavSectionHeader>Apps</NavSectionHeader>
          <NavItem
            onClick={() => props.setContent(<ByColorForm />)}
            icon={<FormulaByColor />}
            value="2"
          >
            Aggregate by color
          </NavItem>
          <NavItem
            onClick={() => props.setContent(<ReconcilerForm />)}
            icon={<LightbulbPerson />}
            value="3"
          >
            Reconciler
          </NavItem>
          <NavDivider />
          <NavSectionHeader>Custom functions</NavSectionHeader>
          <NavCategory value="4">
            <NavCategoryItem icon={<SettingsCogMultiple />}>Bolt-ons</NavCategoryItem>
            <NavSubItemGroup>
              <NavSubItem href="https://youtu.be/vrnrJ7NWXhE" value="5">
                STATICRANDBETWEEN
              </NavSubItem>
            </NavSubItemGroup>
          </NavCategory>
          <NavCategory value="6">
            <NavCategoryItem icon={<MoneyCalculator />}>Finance & Accounting</NavCategoryItem>
            <NavSubItemGroup>
              <NavSubItem href="" value="7">
                GROSSDOWN
              </NavSubItem>
              <NavSubItem href="" value="8">
                GROSSUP
              </NavSubItem>
            </NavSubItemGroup>
          </NavCategory>
          <NavDivider />
          <NavSectionHeader>Help</NavSectionHeader>
          <NavItem icon={<Premium20Filled />} href="https://technbash.com/faqs/" value="9">
            Go Premium
          </NavItem>
          <NavItem icon={<ChatMail />} href="mailto:hello@technbash.com" value="10">
            Contact Us
          </NavItem>
          <NavItem icon={<ChatBubbleQuestion />} href="https://technbash.com/faqs/" value="11">
            FAQs
          </NavItem>
          <NavItem
            icon={<Video />}
            href="https://www.youtube.com/@techandbash/playlists"
            value="12"
          >
            Demos
          </NavItem>
        </NavDrawerBody>
      </NavDrawer>
      <div className={styles.content}>
        <Tooltip content="Toggle navigation pane" relationship="label">
          <Hamburger
            onClick={() => setIsOpen(!isOpen)}
            {...restoreFocusTargetAttributes}
            aria-expanded={isOpen}
          />
        </Tooltip>
        <div className={styles.innerContent}>{props.content}</div>
      </div>
    </div>
  );
};

export default BasicNav;
